import type { Response as SuperAgentResponse, SuperAgentRequest } from 'superagent';
import request from '../../util/SuperagentUtils';
import { bookingRequests } from '../../requests/api-endpoints';
import { csrfFetch } from '../../util/CsrfProtection';

/**
 * Body of POST /api/booking and /api/booking/calculate-booking-charge-amount (the API's BookingInputVO).
 * Each flow (marketplace checkout, brokered booking, cost preview) sends a different subset of the optional fields.
 */
export interface BookingInput {
  id?: string;
  supplierAccountId: string;
  buyerAccountId: string;
  locationId: string;
  numberOfSpaces: number;
  startDate: string;
  endDate: string;
  frequency: string;
  recurrences: number;
  durationType: string;
  assetType: string;
  brokeredBooking: boolean;
  paymentMethodId?: string | null;
  email?: string | null;
  tosDocUrl?: string;
  source?: string;
  attributionSource?: string | null;
  chargeOverages?: boolean;
  manualPayment?: boolean;
  ignoreDate?: boolean;
  brokeredInitialChargeAlreadyPaid?: boolean;
  brokeredRecurringChargeAlreadyPaid?: boolean;
  brokeredSupplierPaidPerOccurrence?: number;
  brokeredBuyerChargedPerOccurrence?: number;
  brokeredSupplierOverageRatePaid?: number;
  brokeredBuyerOverageRateCharged?: number;
}

/** Body of the endpoints that act on a single booking, e.g. the API's BookingCancellationVO. */
interface BookingIdRequest {
  bookingId: string;
}

/** Mirrors the API's BookingCompletionVO. */
interface CompleteBookingRequest extends BookingIdRequest {
  tosDocUrl: string;
}

/** Mirrors the API's DateAdjustVO and EndDateAdjustVO. */
interface AdjustEndDateRequest extends BookingIdRequest {
  endDate: string;
}

/** Mirrors the API's UpdateSpaceCountVO. */
interface AdjustNumberOfSpacesRequest extends BookingIdRequest {
  numberOfSpaces: number;
  brokeredSupplierPaidPerOccurrence?: number;
  brokeredBuyerChargedPerOccurrence?: number;
}

/** Mirrors the API's BookingPaymentVO. */
interface UpdateBookingPaymentMethodRequest extends BookingIdRequest {
  paymentMethodId: string;
}

/** Mirrors the API's BookingStatusByTokenVO, as sent when a supplier approves or declines from an email link. */
interface BookingStatusByTokenRequest {
  status: 'APPROVED' | 'DECLINED';
  reason: string | null;
  notes?: string;
}

const handleResponse = (response: Response): Promise<string> => {
  if (!response.ok) {
    throw Error(`${response.status} - ${response.statusText}`);
  }
  return response.text();
};

export const sendBookingConfirmationEmail = async (bookingId: string): Promise<string> => {
  const response = await csrfFetch(bookingRequests.sendConfirmationEmail, {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      "Accept": "application/json",
    },
    body:  JSON.stringify({bookingId}),
  });
  return handleResponse(response);
};

export const bookSpace = (bookingRequest: BookingInput): SuperAgentRequest => {
  return request.post(bookingRequests.create).send(bookingRequest);
};

export const cancelBooking = (payload: BookingIdRequest): SuperAgentRequest => {
  return request.post(bookingRequests.cancel).send(payload);
};

export const cancelBookingSubscription = (payload: BookingIdRequest): SuperAgentRequest => {
  return request.post(bookingRequests.cancelSubscription).send(payload);
};

export const calculateBookingChargeAmount = (bookingData: BookingInput): SuperAgentRequest => {
  return request.post(bookingRequests.calculateBookingChargeAmount).send(bookingData);
};

export const completeBooking = async (bookingData: CompleteBookingRequest): Promise<SuperAgentResponse> => {
  const payload = {
    bookingId: bookingData?.bookingId,
    tosDocUrl: bookingData?.tosDocUrl
  };
  return request.post(bookingRequests.completeBooking).send(payload);
};

export const getBookingInfo = async (bookingId: string): Promise<SuperAgentResponse> => {
  return request.get(bookingRequests.getBookingById(bookingId));
};

export const adjustEndDate = (adjustDateRequest: AdjustEndDateRequest): SuperAgentRequest => {
  return request.post(bookingRequests.adjustDates).send(adjustDateRequest);
};

export const adjustEndDateRecurring = (adjustDateRequest: AdjustEndDateRequest): SuperAgentRequest => {
  return request.post(bookingRequests.adjustEndDate).send(adjustDateRequest);
};

export const adjustNumberOfSpaces = (adjustNumberOfSpacesRequest: AdjustNumberOfSpacesRequest): SuperAgentRequest => {
  return request.post(bookingRequests.adjustNumberOfSpaces).send(adjustNumberOfSpacesRequest);
};

export const updatePaymentMethodForBooking = (updatePaymentData: UpdateBookingPaymentMethodRequest): SuperAgentRequest => {
  return request.post(bookingRequests.updateBookingPaymentMethod).send(updatePaymentData)
};

export const getBookingByToken = (token: string): SuperAgentRequest => {
  return request.get(bookingRequests.bookingByToken(token));
};

export const approveOrDeclineByToken = (token: string, payload: BookingStatusByTokenRequest): SuperAgentRequest => {
  return request.post(bookingRequests.bookingByToken(token)).send(payload)
}
